import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { CalendarDays, Building2, Search, Check, ChevronsUpDown } from 'lucide-react';
import { useSuppliers } from '@/hooks/useSuppliers';
import { cn } from '@/lib/utils';

interface SupplierQuickSelectProps {
  supplierCode: string;
  deliveryDate: string;
  onSupplierChange: (code: string) => void;
  onDateChange: (date: string) => void;
}

export const SupplierQuickSelect = ({
  supplierCode,
  deliveryDate,
  onSupplierChange,
  onDateChange
}: SupplierQuickSelectProps) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { data: suppliers = [] } = useSuppliers();

  // Handle supplier selection
  const handleSupplierSelect = (code: string) => {
    onSupplierChange(code);
    setOpen(false);
    setSearchTerm('');
  };

  // Filter suppliers based on search
  const activeSuppliers = suppliers.filter(s => s.is_active && s.supplier_code);
  const filteredSuppliers = activeSuppliers.filter(supplier =>
    supplier.supplier_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supplier.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get selected supplier
  const selectedSupplier = activeSuppliers.find(s => s.supplier_code === supplierCode);

  // Quick date options
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const quickDates = [
    { label: 'Yesterday', value: yesterday.toISOString().split('T')[0] },
    { label: 'Today', value: today.toISOString().split('T')[0] },
    { label: 'Tomorrow', value: tomorrow.toISOString().split('T')[0] }
  ];

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <Building2 className="w-12 h-12 mx-auto text-primary" />
        <h2 className="text-2xl font-semibold">Select Supplier</h2>
        <p className="text-muted-foreground">Choose supplier and delivery date</p>
      </div>

      {/* Selected Supplier Display */}
      {selectedSupplier && (
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
                        <div>
                          <div className="font-mono text-lg font-semibold">{selectedSupplier.supplier_code}</div>
                        </div>
              <Badge variant="secondary">Selected</Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Supplier Dropdown */}
      {!selectedSupplier && (
        <div className="space-y-4">
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={open}
                className="w-full h-14 justify-between text-left font-normal text-lg"
              >
                <span className="flex items-center gap-3">
                  <Search className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Select supplier...</span>
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0" align="start">
              <Command>
                <CommandInput 
                  placeholder="Search suppliers by code..." 
                  value={searchTerm}
                  onValueChange={setSearchTerm}
                  className="h-12"
                />
                <CommandList className="max-h-64">
                  <CommandEmpty>No suppliers found.</CommandEmpty>
                  <CommandGroup>
                    {filteredSuppliers.map((supplier) => (
                      <CommandItem
                        key={supplier.supplier_code}
                        value={supplier.supplier_code}
                        onSelect={() => handleSupplierSelect(supplier.supplier_code!)}
                        className="py-3 cursor-pointer"
                      >
                        <div className="flex items-center justify-between w-full">
                          <span className="font-mono text-lg">{supplier.supplier_code}</span>
                          <Check
                            className={cn(
                              "ml-auto h-4 w-4",
                              supplierCode === supplier.supplier_code ? "opacity-100" : "opacity-0"
                            )}
                          />
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      )}

      {/* Change Supplier Button */}
      {selectedSupplier && (
        <Button
          variant="outline"
          onClick={() => onSupplierChange('')}
          className="w-full"
        >
          Change Supplier
        </Button>
      )}

      {/* Delivery Date Selection */}
      <div className="space-y-4">
        <Label className="flex items-center gap-2 text-base font-medium">
          <CalendarDays className="w-4 h-4" />
          Delivery Date
        </Label>
        
        {/* Quick Date Buttons */}
        <div className="grid grid-cols-3 gap-2">
          {quickDates.map((date) => (
            <Button
              key={date.value}
              variant={deliveryDate === date.value ? "default" : "outline"}
              onClick={() => onDateChange(date.value)}
              className="h-12"
            >
              {date.label}
            </Button>
          ))}
        </div>

        {/* Custom Date Input */}
        <div className="relative">
          <Input
            type="date"
            value={deliveryDate}
            onChange={(e) => onDateChange(e.target.value)}
            className="text-lg h-12"
          />
        </div>
      </div>
    </div>
  );
};